import {SketchEntityKind} from './SketchEntityKinds';
import {
    allowAppendSketchRelationTarget,
    getSketchRelationCategory,
    isCreatableSketchRelationKind,
    SketchRelationCategory,
    SketchRelationKind,
} from './SketchRelationKinds';
import {resolveSketchRelationDisplayName} from './SketchRelationDefinitions';
import {SKETCH_RELATION_RULES} from './SketchRelationRules';
import type {
    NormalizedSelection,
    RelationCandidate,
    RelationRole,
    RelationRule,
    RelationSignature,
    RelationValidationContext,
    ValidationResult,
} from './SketchRelationTypes';
import {ISketchObject} from "../../../../mesh/sketch";
import type {SketchRelationSnapshot} from '../../../../../types/sketch/solver/constraint/SketchRelationContainer';

/**
 * 共有约束视图模型。
 * 表示被所有选中对象共同持有的一条约束。
 */
export interface SharedConstraintItem {
    /** 约束 ID。 */
    constraintId: number;
    /** 约束显示名称。 */
    displayName: string;
    /** 关系类型。 */
    relationType: unknown;
    /** 是否为不可按普通关系删除的派生视图。 */
    isReadOnly?: boolean;
    /** 派生视图的来源尺寸 ID。 */
    sourceDimensionId?: string;
}


/**
 * 解析关系类型显示名称。
 * @param type 关系类型。
 * @returns 关系名称。
 */
function resolveRelationDisplayName(type: SketchRelationKind): string {
    // 显示名单点声明在关系定义表，这里只做派生（未登记时回退枚举名）。
    return resolveSketchRelationDisplayName(type);
}



/**
 * 草图关系兼容性判断引擎。
 * 职责：
 * 1. 归一化当前选择实体；
 * 2. 基于规则表返回可创建关系候选；
 * 3. 校验指定关系在当前选择下是否可创建；
 * 4. 提供可测试、无副作用的纯业务能力。
 */
export class SketchRelationCompatibility {
    readonly isSketchRelationCompatibility: boolean = true;
    /**
     * 获取当前选择下可创建的关系候选。
     * @param selection 当前选择实体列表。
     * @returns 去重并按优先级排序后的关系候选列表。
     */
    public getAvailableRelations(selection: ISketchObject[]): RelationCandidate[] {
        const normalized = this.normalizeSelection(selection);

        // 无选择时直接返回空候选。
        if (normalized.length <= 0) {
            return [];
        }

        const candidateMap = new Map<SketchRelationKind, RelationCandidate>();
        const candidatePriorityMap = new Map<SketchRelationKind, number>();

        SKETCH_RELATION_RULES.forEach((rule) => {
            // 已声明但尚无创建/公式/持久化闭环的关系不得出现在可用候选中。
            if (!isCreatableSketchRelationKind(rule.kind)) {
                return;
            }
            const validation = this.evaluateRule(rule, normalized);
            if (!validation.ok) {
                return;
            }

            const category = rule.category ?? getSketchRelationCategory(rule.kind);
            const allowAppend = typeof rule.allowAppend === 'boolean'
                ? rule.allowAppend
                : allowAppendSketchRelationTarget(rule.kind);
            const candidatePriority = rule.priority ?? 100;

            const candidate: RelationCandidate = {
                kind: rule.kind,
                // 为候选关系附带显示名称，供界面直接渲染。
                displayName: resolveRelationDisplayName(rule.kind),
                category,
                allowAppend,
            };

            const existing = candidateMap.get(rule.kind);
            if (!existing) {
                candidateMap.set(rule.kind, candidate);
                candidatePriorityMap.set(rule.kind, candidatePriority);
                return;
            }

            const existingPriority = candidatePriorityMap.get(rule.kind) ?? Number.POSITIVE_INFINITY;

            // 同一关系被多条规则命中时，保留优先级更高（数值更小）的候选。
            if (candidatePriority < existingPriority) {
                candidateMap.set(rule.kind, candidate);
                candidatePriorityMap.set(rule.kind, candidatePriority);
                return;
            }

            // 同一关系多规则命中时，若分类/追加能力冲突，则收敛到更严格配置。
            if (existing.category !== candidate.category || existing.allowAppend !== candidate.allowAppend) {
                const mergedCategory = (
                    existing.category === SketchRelationCategory.Self
                    || candidate.category === SketchRelationCategory.Self
                )
                    ? SketchRelationCategory.Self
                    : SketchRelationCategory.Multi;

                candidateMap.set(rule.kind, {
                    ...existing,
                    category: mergedCategory,
                    allowAppend: existing.allowAppend && candidate.allowAppend,
                });
            }
        });

        return Array.from(candidateMap.values()).sort((left, right) => {
            const leftPriority = candidatePriorityMap.get(left.kind) ?? Number.POSITIVE_INFINITY;
            const rightPriority = candidatePriorityMap.get(right.kind) ?? Number.POSITIVE_INFINITY;

            if (leftPriority !== rightPriority) {
                return leftPriority - rightPriority;
            }

            return left.kind.localeCompare(right.kind);
        });
    }

    /**
     * 校验指定关系是否可创建。
     * @param kind 关系类型。
     * @param selection 当前选择实体列表。
     * @returns 校验结果。
     */
    public canCreateRelation(kind: SketchRelationKind, selection: ISketchObject[]): ValidationResult {
        if (!isCreatableSketchRelationKind(kind)) {
            return {
                ok: false,
                reason: `关系 ${kind} 尚未具备生产创建闭环`,
            };
        }
        const normalized = this.normalizeSelection(selection);
        const rules = SKETCH_RELATION_RULES.filter((rule) => rule.kind === kind);

        if (rules.length <= 0) {
            return {
                ok: false,
                reason: '未找到对应关系规则',
            };
        }

        const reasons: string[] = [];

        for (let index = 0; index < rules.length; index += 1) {
            const rule = rules[index];
            const validation = this.evaluateRule(rule, normalized);

            if (validation.ok) {
                return {ok: true};
            }

            if (validation.reason) {
                reasons.push(validation.reason);
            }
        }

        return {
            ok: false,
            reason: this.mergeReasons(reasons) || '当前选择与关系规则不兼容',
        };
    }

    /**
     * 获取推荐默认关系。
     * @param selection 当前选择实体列表。
     * @returns 默认候选关系；若不存在则返回 null。
     */
    public getBestDefaultRelation(selection: ISketchObject[]): RelationCandidate | null {
        const candidates = this.getAvailableRelations(selection);
        return candidates[0] ?? null;
    }

    /**
     * 获取所有选中对象共有的约束列表。
     * 逻辑：取每个对象约束快照的 ID 交集，返回交集中的约束详情。
     * @param selection 当前选择实体列表。
     * @returns 所有选中对象共同持有的约束列表。
     */
    public getSharedConstraints(selection: ISketchObject[]): SharedConstraintItem[] {
        if (!Array.isArray(selection) || selection.length <= 0) {
            return [];
        }

        // 收集每个对象的约束快照列表
        const snapshotsList: SketchRelationSnapshot[][] = selection.map((obj) => {
            return obj.constraints?.getRelationSnapshots() ?? [];
        });

        // 以第一个对象的约束 ID 集合为基础，逐步与后续对象取交集
        let sharedIds = new Set<number>(snapshotsList[0].map((s) => s.id));
        for (let i = 1; i < snapshotsList.length; i++) {
            const currentIds = new Set<number>(snapshotsList[i].map((s) => s.id));
            sharedIds = new Set<number>([...sharedIds].filter((id) => currentIds.has(id)));
        }

        if (sharedIds.size <= 0) {
            return [];
        }

        // 从第一个对象的快照中取出交集约束详情
        const snapshotMap = new Map<number, SketchRelationSnapshot>();
        for (const snapshot of snapshotsList[0]) {
            if (sharedIds.has(snapshot.id)) {
                snapshotMap.set(snapshot.id, snapshot);
            }
        }

        // 按约束 ID 排序，保证输出稳定
        return Array.from(snapshotMap.values())
            .sort((a, b) => a.id - b.id)
            .map((snapshot) => ({
                constraintId: snapshot.id,
                displayName: snapshot.baseDisplayName || snapshot.name || String(snapshot.id),
                relationType: snapshot.relationType,
                isReadOnly: snapshot.isReadOnly,
                sourceDimensionId: snapshot.sourceDimensionId,
            }));
    }

    /**
     * 归一化选择集合。
     * @param selection 当前选择实体列表。
     * @returns 归一化后的选择列表。
     */
    public normalizeSelection(selection: ISketchObject[]): NormalizedSelection[] {
        if (!Array.isArray(selection) || selection.length <= 0) {
            return [];
        }

        return selection.map((item, index) => {
            const rawKind = item?.objectType;
            const kind = this.isKnownKind(rawKind) ? rawKind : SketchEntityKind.Point;
            const baseKind = this.resolveBaseKind(kind);

            // 预计算常用布尔标签，避免后续规则重复判断。
            const isPoint = baseKind === SketchEntityKind.Point;
            const isLine = baseKind === SketchEntityKind.Line;
            const isCenterLine = kind === SketchEntityKind.CenterLine;
            const isArcLike = kind === SketchEntityKind.Arc || kind === SketchEntityKind.Circle;
            const isCircleLike = kind === SketchEntityKind.Circle || kind === SketchEntityKind.Arc;
            const isPlaneLike = kind === SketchEntityKind.ReferencePlane || kind === SketchEntityKind.PlanarFace;
            const isEdgeLike = kind === SketchEntityKind.ModelEdge;
            const isAxisLike = kind === SketchEntityKind.ReferenceAxis;
            const isCurve = (
                kind === SketchEntityKind.Line
                || kind === SketchEntityKind.CenterLine
                || kind === SketchEntityKind.Arc
                || kind === SketchEntityKind.Circle
                || kind === SketchEntityKind.Ellipse
                || kind === SketchEntityKind.Spline
                || kind === SketchEntityKind.Slot
            );

            return {
                id: item.uuid,
                kind,
                baseKind,
                isPoint,
                isLine,
                isCenterLine,
                isCurve,
                isArcLike,
                isCircleLike,
                isPlaneLike,
                isEdgeLike,
                isAxisLike,
            };
        });
    }

    /**
     * 评估单条规则是否命中。
     * @param rule 关系规则。
     * @param selection 归一化后的选择列表。
     * @returns 校验结果。
     */
    private evaluateRule(rule: RelationRule, selection: NormalizedSelection[]): ValidationResult {
        if (selection.length < rule.minCount) {
            return {
                ok: false,
                reason: `至少需要选择 ${rule.minCount} 个实体`,
            };
        }

        if (typeof rule.maxCount === 'number' && selection.length > rule.maxCount) {
            return {
                ok: false,
                reason: `最多允许选择 ${rule.maxCount} 个实体`,
            };
        }

        let signatureMatched = false;
        let signatureReason = '';

        for (let index = 0; index < rule.signatures.length; index += 1) {
            const signature = rule.signatures[index];
            const matched = signature.ordered
                ? this.matchOrderedSignature(selection, signature)
                : this.matchUnorderedSignature(selection, signature);

            if (matched) {
                signatureMatched = true;
                break;
            }

            if (!signatureReason && signature.reason) {
                signatureReason = signature.reason;
            }
        }

        if (!signatureMatched) {
            return {
                ok: false,
                reason: signatureReason || rule.failureReason || '当前选择不满足关系角色要求',
            };
        }

        if (rule.validator) {
            const customValidation = rule.validator(
                selection,
                this.createValidationContext(selection),
            );
            if (!customValidation.ok) {
                return customValidation;
            }
        }

        return {ok: true};
    }

    /**
     * 创建关系规则自定义校验上下文。
     * @param selection 归一化后的选择列表。
     * @returns 校验上下文。
     */
    private createValidationContext(selection: readonly NormalizedSelection[]): RelationValidationContext {
        return {
            hasReferencePlaneContext: selection.some((item) => item.isPlaneLike),
        };
    }

    /**
     * 无序签名匹配。
     * @param selection 归一化后的选择列表。
     * @param signature 关系签名。
     * @returns 是否匹配成功。
     */
    private matchUnorderedSignature(
        selection: NormalizedSelection[],
        signature: RelationSignature,
    ): boolean {
        const roleCounts = new Array<number>(signature.roles.length).fill(0);
        const roleMax = signature.roles.map((role) => role.max ?? Number.POSITIVE_INFINITY);
        const roleMin = signature.roles.map((role) => role.min ?? 1);

        const backtrack = (selectionIndex: number): boolean => {
            if (selectionIndex >= selection.length) {
                for (let roleIndex = 0; roleIndex < roleMin.length; roleIndex += 1) {
                    if (roleCounts[roleIndex] < roleMin[roleIndex]) {
                        return false;
                    }
                }
                return true;
            }

            const current = selection[selectionIndex];

            for (let roleIndex = 0; roleIndex < signature.roles.length; roleIndex += 1) {
                if (roleCounts[roleIndex] >= roleMax[roleIndex]) {
                    continue;
                }

                const role = signature.roles[roleIndex];
                if (!this.matchRole(current, role)) {
                    continue;
                }

                roleCounts[roleIndex] += 1;

                if (backtrack(selectionIndex + 1)) {
                    return true;
                }

                roleCounts[roleIndex] -= 1;
            }

            return false;
        };

        return backtrack(0);
    }

    /**
     * 有序签名匹配。
     * @param selection 归一化后的选择列表。
     * @param signature 关系签名。
     * @returns 是否匹配成功。
     */
    private matchOrderedSignature(
        selection: NormalizedSelection[],
        signature: RelationSignature,
    ): boolean {
        const roleMin = signature.roles.map((role) => role.min ?? 1);
        const roleMax = signature.roles.map((role) => role.max ?? Number.POSITIVE_INFINITY);

        let cursor = 0;

        for (let roleIndex = 0; roleIndex < signature.roles.length; roleIndex += 1) {
            const role = signature.roles[roleIndex];
            let matchedCount = 0;

            while (
                cursor < selection.length
                && matchedCount < roleMax[roleIndex]
                && this.matchRole(selection[cursor], role)
            ) {
                matchedCount += 1;
                cursor += 1;
            }

            if (matchedCount < roleMin[roleIndex]) {
                return false;
            }
        }

        return cursor === selection.length;
    }

    /**
     * 判断单个实体是否匹配角色定义。
     * @param item 归一化实体。
     * @param role 角色定义。
     * @returns 是否匹配。
     */
    private matchRole(item: NormalizedSelection, role: RelationRole): boolean {
        if (role.kinds && role.kinds.length > 0) {
            const kindMatched = role.kinds.includes(item.kind) || role.kinds.includes(item.baseKind);
            if (!kindMatched) {
                return false;
            }
        }

        if (role.predicate && !role.predicate(item)) {
            return false;
        }

        return true;
    }

    /**
     * 解析实体基础类型。
     * @param kind 原始实体类型。
     * @returns 基础实体类型。
     */
    private resolveBaseKind(kind: SketchEntityKind): SketchEntityKind {
        if (kind === SketchEntityKind.EndPoint) {
            return SketchEntityKind.Point;
        }

        if (kind === SketchEntityKind.CenterLine) {
            return SketchEntityKind.Line;
        }

        return kind;
    }

    /**
     * 判断输入是否为已知实体类型。
     * @param value 原始类型输入。
     * @returns 是否为已知类型。
     */
    private isKnownKind(value: unknown): value is SketchEntityKind {
        return Object.values(SketchEntityKind).includes(value as SketchEntityKind);
    }

    /**
     * 合并失败原因列表。
     * @param reasons 原因集合。
     * @returns 去重后的原因文本。
     */
    private mergeReasons(reasons: string[]): string {
        const unique = Array.from(new Set(reasons.filter((item) => Boolean(item))));
        return unique.join('；');
    }
}
